import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { usePostAuthorization, useSubscribeToNotifications } from "@sprout/client-components/dist/Post";
import RenderIfChildExists from "@sprout/client-components/dist/RenderIfChildExists";
import { MenuItemShare, useMenuItemPageCopy } from "@sprout/client-components/dist/MenuItemReusable";
import useCurrentUser from "@sprout/client-graphql/dist/useCurrentUser";
import { MenuGrid, MenuTitle, MenuItem } from "@sprout/list-navigation/dist/Menu";
import { useCallback, useMemo } from "react";
import { ItemFocusWhen, ItemType, useSetItemsFocusWhenMutation } from "sprout-graphql";
import { ArchiveOutlined, DeleteOutlined, NotificationsNoneOutlined, NotificationsOffOutlined, OrderBoolAscendingVariant, RestoreFromTrashOutlined, UnarchiveOutlined, PriorityHighOutlined } from "sprout-ui-icons";
import useToggle from "@sprout/hooks/dist/useToggle";
import { MoveItemsDialog } from "@sprout/client-components/dist/Dialog_next";
import { useActionRegistryGlobalHandler, useActionRegistryNavigation } from "@sprout/list-navigation";
import { getItemUrl } from "sprout-lib";
import { useSnackbar } from "sprout-ui-dom/dist/Snackbar";
import { PostPageToolbarMenuCreatedAt, PostPageToolbarMenuViewMode, PostPageToolbarMenuPopover } from "../PostPageToolbar";
const PostPageViewsPostToolbarMenu = (props)=>{
    const { post  } = props;
    const { id , nodeId , isExplicitlyArchived , isExplicitlyDeleted , userPreferences , createdAt , owner , rootTopicOrSelf ,  } = post;
    const currentUser = useCurrentUser();
    const authorization = usePostAuthorization(post);
    const { focusWhen , subscribeToNotifications , nodeId: userPreferencesNodeId ,  } = userPreferences || {};
    const isSetAside = focusWhen === ItemFocusWhen.Later;
    const isFocusNow = focusWhen === ItemFocusWhen.Now;
    //
    const selectedItems = useMemo(()=>[
            post
        ], [
        post
    ]);
    const [move, openMove, closeMove] = useToggle();
    useActionRegistryGlobalHandler("PAGE_MOVE", useCallback(()=>{
        openMove();
        return true;
    }, [
        openMove
    ]), {
        skip: !authorization.move || !rootTopicOrSelf,
        // Required in Chrome, prevent the letter `m` from filling the input
        preventDefault: true
    });
    //
    const showSnackbar = useSnackbar();
    const [setItemsFocusWhen] = useSetItemsFocusWhenMutation();
    const { setAside , setDone  } = useMemo(()=>{
        const setFocusWhen = (itemFocusWhen)=>{
            return ()=>{
                setItemsFocusWhen({
                    variables: {
                        userItemPreferences: [
                            id
                        ].map((itemId)=>({
                                itemId,
                                focusWhen: itemFocusWhen
                            }))
                    }
                }).catch(()=>{
                    showSnackbar.error();
                });
                return true;
            };
        };
        return {
            setFocusNow: setFocusWhen(ItemFocusWhen.Now),
            setAside: setFocusWhen(ItemFocusWhen.Later),
            setDone: setFocusWhen(null)
        };
    }, [
        id,
        setItemsFocusWhen,
        showSnackbar
    ]);
    useActionRegistryGlobalHandler("PAGE_SET_SET_ASIDE", useCallback(()=>setAside(), [
        setAside
    ]), {
        skip: !authorization.bookmark || isSetAside
    });
    useActionRegistryGlobalHandler("PAGE_SET_DONE", useCallback(()=>setDone(), [
        setDone
    ]), {
        skip: !authorization.bookmark || !isFocusNow && !isSetAside
    });
    //
    const subscribeToNotificationsMutation = useSubscribeToNotifications();
    const handleSubscribeToNotifications = useCallback(()=>{
        subscribeToNotificationsMutation({
            variables: {
                postId: id,
                subscribeToNotifications: !subscribeToNotifications
            },
            nodeId,
            userPreferencesNodeId
        });
    }, [
        id,
        nodeId,
        subscribeToNotifications,
        subscribeToNotificationsMutation,
        userPreferencesNodeId, 
    ]);
    //
    const activityURL = `${getItemUrl(post)}/activity`;
    useActionRegistryNavigation("PAGE_VIEW_ITEM_ACTIVITY", activityURL, {
        skip: !authorization.viewActivity
    });
    //
    const [, MenuItemCopy] = useMenuItemPageCopy();
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            authorization.move && /*#__PURE__*/ _jsx(MoveItemsDialog, {
                open: move,
                onRequestClose: closeMove,
                selectedItems: selectedItems
            }),
            /*#__PURE__*/ _jsxs(PostPageToolbarMenuPopover, {
                children: [
                    /*#__PURE__*/ _jsx(MenuTitle, {
                        children: "Leaf options"
                    }),
                    /*#__PURE__*/ _jsxs(RenderIfChildExists, {
                        component: /*#__PURE__*/ _jsx(MenuGrid, {}),
                        children: [
                            authorization.move && /*#__PURE__*/ _jsx(MenuItem, {
                                variant: "tile",
                                action: "PAGE_MOVE"
                            }),
                            authorization.addLabel && /*#__PURE__*/ _jsx(MenuItem, {
                                variant: "tile",
                                action: "PAGE_LABEL"
                            }),
                            authorization.assign && /*#__PURE__*/ _jsx(MenuItem, {
                                variant: "tile",
                                action: "PAGE_ASSIGN"
                            }),
                            authorization.archive && /*#__PURE__*/ _jsx(MenuItem, {
                                variant: "tile",
                                action: "PAGE_TOGGLE_ARCHIVE",
                                icon: isExplicitlyArchived ? UnarchiveOutlined : ArchiveOutlined,
                                label: isExplicitlyArchived ? "Unarchive" : "Archive"
                            })
                        ]
                    }),
                    authorization.schedule && /*#__PURE__*/ _jsx(MenuItem, {
                        action: "PAGE_DEADLINE"
                    }),
                    currentUser && /*#__PURE__*/ _jsx(MenuItem, {
                        label: "Priority",
                        icon: PriorityHighOutlined,
                        action: "PAGE_TOGGLE_PRIORITY"
                    }),
                    authorization.addTodoList && /*#__PURE__*/ _jsx(MenuItem, {
                        action: "ADD_TODOLIST",
                        label: "To-do list heading",
                        icon: OrderBoolAscendingVariant
                    }),
                    authorization.bookmark && /*#__PURE__*/ _jsx(MenuItem, {
                        action: isSetAside ? "PAGE_SET_DONE" : "PAGE_SET_SET_ASIDE"
                    }),
                    /*#__PURE__*/ _jsx(PostPageToolbarMenuViewMode, {}),
                    /*#__PURE__*/ _jsx(MenuItemCopy, {
                        type: "leaf"
                    }),
                    /*#__PURE__*/ _jsx(MenuItemShare, {
                        type: ItemType.Post
                    }),
                    currentUser && /*#__PURE__*/ _jsx(MenuItem, {
                        label: `Turn ${subscribeToNotifications ? "off" : "on"} leaf notifications`,
                        icon: subscribeToNotifications ? NotificationsOffOutlined : NotificationsNoneOutlined,
                        onClick: handleSubscribeToNotifications
                    }),
                    authorization.viewActivity && /*#__PURE__*/ _jsx(MenuItem, {
                        label: "View leaf activity",
                        action: "PAGE_VIEW_ITEM_ACTIVITY"
                    }),
                    authorization.trash && /*#__PURE__*/ _jsx(MenuItem, {
                        action: "PAGE_TOGGLE_DELETE",
                        label: isExplicitlyDeleted ? "Restore" : "Trash",
                        icon: isExplicitlyDeleted ? RestoreFromTrashOutlined : DeleteOutlined
                    }),
                    authorization.delete && /*#__PURE__*/ _jsx(MenuItem, {
                        action: "PAGE_PERMANENT_DELETE"
                    }),
                    owner && /*#__PURE__*/ _jsx(PostPageToolbarMenuCreatedAt, {
                        createdAt: createdAt,
                        owner: owner
                    })
                ]
            })
        ]
    });
};
export default PostPageViewsPostToolbarMenu;
